const MARKETING_API_URL = "/api/marketing"
const ADMIN_KEY = process.env.NEXT_PUBLIC_ADMIN_API_KEY || "24hr-mktg-admin-key-2026"

interface ApiResponse<T = unknown> {
  success: boolean
  message: string
  data?: T
}

export interface MarketingAuthResponse {
  token?: string
  user?: Record<string, unknown>
  authSource?: string
  mainPortalToken?: string
  requiresTwoFactor?: boolean
  provider?: string
  email?: string
}

async function request<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  const token = typeof window !== "undefined" ? localStorage.getItem("auth_token") : null
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    "X-Admin-Key": ADMIN_KEY,
    ...((options.headers as Record<string, string>) || {}),
  }

  if (token) {
    headers.Authorization = `Bearer ${token}`
  }

  const res = await fetch(`${MARKETING_API_URL}${endpoint}`, {
    ...options,
    headers,
  })

  const json = await res.json()

  if (!res.ok) {
    throw new Error(json.message || "Something went wrong")
  }

  return json
}

export interface Client {
  _id: string
  email: string
  firstName: string
  lastName: string
  role: string
  isActive: boolean
  createdAt: string
}

export async function getClients(params?: {
  page?: number
  limit?: number
  search?: string
}): Promise<ApiResponse<{ clients: Client[]; total: number }>> {
  const query = new URLSearchParams()
  if (params?.page) query.set("page", String(params.page))
  if (params?.limit) query.set("limit", String(params.limit))
  if (params?.search) query.set("search", params.search)
  const qs = query.toString()
  return request(`/clients${qs ? `?${qs}` : ""}`)
}

export async function createClient(payload: {
  email: string
  firstName?: string
  lastName?: string
}): Promise<ApiResponse<{ user: Client; passwordSent: boolean }>> {
  return request("/clients", {
    method: "POST",
    body: JSON.stringify(payload),
  })
}

export async function deleteClient(id: string): Promise<ApiResponse<null>> {
  return request(`/clients/${id}`, { method: "DELETE" })
}

export interface ClientAiAccess {
  clientId: string
  email?: string
  firstName?: string
  lastName?: string
  clientCode: string
}

export async function getClientAiAccessCode(id: string): Promise<ApiResponse<ClientAiAccess>> {
  return request(`/clients/${id}/ai-access`)
}

// Campaign APIs

export interface CampaignTargeting {
  ageMin?: number
  ageMax?: number
  gender?: "all" | "male" | "female"
  locations?: string[]
  interests?: string[]
  devices?: string[]
  customAudiences?: string[]
}

export interface CampaignCreative {
  primaryText?: string
  headline?: string
  callToAction?: string
  destinationUrl?: string
  mediaUrls?: string[]
}

export interface Campaign {
  _id: string
  name: string
  description: string
  status: string
  type: string
  objective: string
  platforms: string[]
  targeting: CampaignTargeting
  creative: CampaignCreative
  budgetType: "daily" | "lifetime"
  budget?: number
  spendCap?: number
  bidStrategy: "automatic" | "manual"
  startDate?: string
  endDate?: string
  tags: string[]
  spent: number
  impressions: number
  clicks: number
  leads: number
  conversions: number
  clusterId?: string
  selectedMailboxIds?: string[]
  clientId: string | { _id: string; firstName: string; lastName: string; email: string }
  createdBy: string | { _id: string; firstName: string; lastName: string; email: string }
  createdAt: string
  updatedAt: string
}

export async function getCampaigns(params?: {
  status?: string
  type?: string
  clientId?: string
  page?: number
  limit?: number
}): Promise<ApiResponse<{ campaigns: Campaign[]; total: number; page: number; limit: number }>> {
  const query = new URLSearchParams()
  if (params?.status) query.set("status", params.status)
  if (params?.type) query.set("type", params.type)
  if (params?.clientId) query.set("clientId", params.clientId)
  if (params?.page) query.set("page", String(params.page))
  if (params?.limit) query.set("limit", String(params.limit))
  const qs = query.toString()
  return request(`/campaigns${qs ? `?${qs}` : ""}`)
}

export async function createCampaign(payload: {
  name: string
  description?: string
  status?: string
  type?: string
  objective?: string
  platforms?: string[]
  targeting?: CampaignTargeting
  creative?: CampaignCreative
  budgetType?: string
  budget?: number
  spendCap?: number
  bidStrategy?: string
  clientId: string
  startDate?: string
  endDate?: string
  tags?: string[]
}): Promise<ApiResponse<Campaign>> {
  return request("/campaigns", {
    method: "POST",
    body: JSON.stringify(payload),
  })
}

export async function getCampaign(id: string, clientId?: string): Promise<ApiResponse<Campaign>> {
  const query = new URLSearchParams()
  if (clientId) query.set("clientId", clientId)
  const qs = query.toString()
  return request(`/campaigns/${id}${qs ? `?${qs}` : ""}`)
}

export interface CampaignFeedbackItem {
  id: string
  kind: "reply" | "failure" | "opt_out" | "feedback"
  channel: "email" | "sms" | "portal"
  source: "message_recipient" | "activity_log" | "inbound_email" | "sms_conversation" | "feedback"
  campaignId: string
  clientId: string
  contactId?: string
  contact?: {
    firstName?: string
    lastName?: string
    email?: string
    phone?: string
    company?: string
  }
  referenceId?: string
  recipientId?: string
  status?: string
  subject?: string
  body?: string
  reason?: string
  metadata?: Record<string, any>
  occurredAt: string
}

export interface CampaignFeedbackSummary {
  total: number
  replies: number
  failures: number
  optOuts: number
  emailReplies: number
  smsReplies: number
  emailFailures: number
  smsFailures: number
  emailOptOuts: number
  smsOptOuts: number
  portalFeedback: number
}

export interface CampaignFeedbackResponse {
  campaignId: string
  clientId: string
  summary: CampaignFeedbackSummary
  feedback: CampaignFeedbackItem[]
}

export async function getCampaignFeedback(id: string, params?: { clientId?: string; limit?: number }) {
  const query = new URLSearchParams()
  if (params?.clientId) query.set("clientId", params.clientId)
  if (params?.limit) query.set("limit", String(params.limit))
  const qs = query.toString()
  return request<CampaignFeedbackResponse>(`/campaigns/${id}/feedback${qs ? `?${qs}` : ""}`)
}

export interface CampaignFunnelStage {
  key: "leads" | "queued" | "sent" | "delivered" | "failed" | "replies" | "optOuts"
  label: string
  value: number
  rate?: number
}

export interface CampaignFunnelResponse {
  campaignId: string
  clientId: string
  leads: number
  queued: number
  sent: number
  delivered: number
  failed: number
  replies: number
  optOuts: number
  replyRate: number
  deliveryRate: number
  failureRate: number
  stages: CampaignFunnelStage[]
}

export async function getCampaignFunnel(id: string, params?: { clientId?: string }) {
  const query = new URLSearchParams()
  if (params?.clientId) query.set("clientId", params.clientId)
  const qs = query.toString()
  return request<CampaignFunnelResponse>(`/campaigns/${id}/funnel${qs ? `?${qs}` : ""}`)
}

export interface CampaignChannelAnalytics {
  total: number
  queued: number
  sent: number
  delivered: number
  opened?: number
  clicked?: number
  failed: number
  bounced?: number
  unsubscribed?: number
  replies: number
  optOuts: number
  deliveryRate: number
  openRate?: number
  clickRate?: number
  replyRate: number
  failureRate: number
  optOutRate: number
}

export interface CampaignAnalyticsTotals {
  campaignId: string
  clientId: string
  spent: number
  budget: number
  impressions: number
  reach: number
  clicks: number
  leads: number
  conversions: number
  ctr: number
  cpc: number
  cpr: number
  cpa: number
  messagesQueued: number
  messagesSent: number
  messagesDelivered: number
  messagesFailed: number
  replies: number
  optOuts: number
  deliveryRate: number
  openRate: number
  clickRate: number
  replyRate: number
  failureRate: number
  optOutRate: number
  email: CampaignChannelAnalytics
  sms: CampaignChannelAnalytics
  social: {
    posts: number
    impressions: number
    reach: number
    clicks: number
    likes: number
    comments: number
    shares: number
  }
  hasData: boolean
  lastEventAt?: string | null
}

export interface CampaignAnalyticsResponse {
  totals: CampaignAnalyticsTotals
  performance: Array<{
    date: string
    impressions: number
    clicks: number
    leads: number
    conversions: number
    spend: number
    emailSent: number
    emailOpened: number
    emailClicked: number
    emailReplies: number
    smsSent: number
    smsDelivered: number
    smsReplies: number
  }>
}

export async function getCampaignAnalytics(id: string, params?: { clientId?: string }) {
  const query = new URLSearchParams()
  if (params?.clientId) query.set("clientId", params.clientId)
  const qs = query.toString()
  return request<CampaignAnalyticsResponse>(`/campaigns/${id}/analytics${qs ? `?${qs}` : ""}`)
}

export async function updateCampaign(id: string, payload: Partial<Campaign>): Promise<ApiResponse<Campaign>> {
  return request(`/campaigns/${id}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  })
}

export async function deleteCampaign(id: string): Promise<ApiResponse<null>> {
  return request(`/campaigns/${id}`, { method: "DELETE" })
}

export async function configureCampaignByotSms(id: string, payload: {
  enabled?: boolean
  accountSid?: string
  authToken?: string
  phoneNumber?: string
}): Promise<ApiResponse<any>> {
  return request(`/campaigns/${id}/byot-sms`, {
    method: "PUT",
    body: JSON.stringify(payload),
  })
}

export async function uploadCampaignMedia(files: File[]): Promise<ApiResponse<{ urls: string[] }>> {
  const formData = new FormData()
  files.forEach((f) => formData.append("files", f))

  const res = await fetch(`${MARKETING_API_URL}/campaigns/upload-media`, {
    method: "POST",
    headers: {
      "X-Admin-Key": ADMIN_KEY,
    },
    body: formData,
  })

  const text = await res.text()
  let json: any = {}
  try {
    json = text ? JSON.parse(text) : {}
  } catch {
    json = { message: text || "Upload failed" }
  }

  if (!res.ok || json?.success === false) {
    throw new Error(json.message || `Upload failed with status ${res.status}`)
  }

  return json
}

export interface Creative {
  _id: string
  name: string
  type: "image" | "video" | "text"
  platform: string
  status: "active" | "paused" | "archived"
  thumbnail?: string
  mediaUrl?: string
  tags?: string[]
  clientId: string
  impressions: number
  clicks: number
  ctr: number
  createdAt: string
}

export async function getCreatives(params?: { clientId?: string; type?: string; q?: string; page?: number; limit?: number }) {
  const query = new URLSearchParams()
  if (params?.clientId) query.set("clientId", params.clientId)
  if (params?.type) query.set("type", params.type)
  if (params?.q) query.set("q", params.q)
  if (params?.page) query.set("page", String(params.page))
  if (params?.limit) query.set("limit", String(params.limit))
  const qs = query.toString()
  return request<{ creatives: Creative[]; total: number; page: number; limit: number }>(`/creatives${qs ? `?${qs}` : ""}`)
}

export async function createCreative(payload: Partial<Creative> & { name: string; clientId: string }) {
  return request<Creative>("/creatives", { method: "POST", body: JSON.stringify(payload) })
}

export async function getCreative(id: string) {
  return request<Creative & { campaignId?: any }>(`/creatives/${id}`)
}

export async function updateCreative(id: string, payload: Partial<Creative> & { campaignId?: string }) {
  return request<Creative>(`/creatives/${id}`, { method: "PUT", body: JSON.stringify(payload) })
}

export async function deleteCreative(id: string) {
  return request(`/creatives/${id}`, { method: "DELETE" })
}

// Leads API

export interface Lead {
  _id: string
  name: string
  email: string
  phone: string
  leadListId?: string
  campaignId: string
  campaignName: string
  platform: string
  status: string
  source: string
  value: number
  clientId: string
  notes: string
  createdAt: string
  updatedAt: string
  tags?: string[]
  emailStatus?: 'pending' | 'valid' | 'invalid' | 'catch-all' | 'unknown' | 'error'
  emailCheckedAt?: string
  emailSmtpCode?: number
  emailSmtpMessage?: string
  emailMxHost?: string
}

export interface LeadList {
  _id: string
  name: string
  normalizedName: string
  description?: string
  clientId: string
  isDefault: boolean
  leadCount?: number
  createdAt: string
  updatedAt: string
}

export async function getLeadLists(params?: { clientId?: string }) {
  const query = new URLSearchParams()
  if (params?.clientId) query.set("clientId", params.clientId)
  const qs = query.toString()
  return request<{ lists: LeadList[] }>(`/leads/lists${qs ? `?${qs}` : ""}`)
}

export async function createLeadList(payload: { name: string; clientId: string; description?: string }) {
  return request<LeadList>("/leads/lists", { method: "POST", body: JSON.stringify(payload) })
}

export async function addLeadsToLeadList(leadListId: string, leadIds: string[]) {
  return request<{ moved: number; matched: number; leadListId: string }>(`/leads/lists/${leadListId}/leads`, {
    method: "POST",
    body: JSON.stringify({ leadIds }),
  })
}

export async function getLeads(params?: { clientId?: string; leadListId?: string; status?: string; platform?: string; campaignId?: string; batchId?: string; search?: string; page?: number; limit?: number }) {
  const query = new URLSearchParams()
  if (params?.clientId) query.set("clientId", params.clientId)
  if (params?.leadListId) query.set("leadListId", params.leadListId)
  if (params?.status) query.set("status", params.status)
  if (params?.platform) query.set("platform", params.platform)
  if (params?.campaignId) query.set("campaignId", params.campaignId)
  if (params?.batchId) query.set("batchId", params.batchId)
  if (params?.search) query.set("search", params.search)
  if (params?.page) query.set("page", String(params.page))
  if (params?.limit) query.set("limit", String(params.limit))
  const qs = query.toString()
  return request<{ leads: Lead[]; total: number; page: number; limit: number }>(`/leads${qs ? `?${qs}` : ""}`)
}

export async function getLeadSelection(params?: { clientId?: string; leadListId?: string; status?: string; platform?: string; campaignId?: string; search?: string; limit?: number }) {
  const query = new URLSearchParams()
  if (params?.clientId) query.set("clientId", params.clientId)
  if (params?.leadListId) query.set("leadListId", params.leadListId)
  if (params?.status) query.set("status", params.status)
  if (params?.platform) query.set("platform", params.platform)
  if (params?.campaignId) query.set("campaignId", params.campaignId)
  if (params?.search) query.set("search", params.search)
  if (params?.limit) query.set("limit", String(params.limit))
  const qs = query.toString()
  return request<{ leads: Lead[]; total: number; returned: number; limit: number }>(`/leads/selection${qs ? `?${qs}` : ""}`)
}

export async function createLead(payload: Partial<Lead> & { name: string; clientId: string }) {
  return request<Lead>("/leads", { method: "POST", body: JSON.stringify(payload) })
}

export async function getLead(id: string) {
  return request<Lead>(`/leads/${id}`)
}

export async function updateLead(id: string, payload: Partial<Lead>) {
  return request<Lead>(`/leads/${id}`, { method: "PUT", body: JSON.stringify(payload) })
}

export async function deleteLead(id: string) {
  return request(`/leads/${id}`, { method: "DELETE" })
}

export async function assignLeadsToCampaign(leadIds: string[], campaignId: string) {
  return request<{ assigned: number; matched: number; campaignId: string; campaignName: string }>("/leads/assign-campaign", {
    method: "POST",
    body: JSON.stringify({ leadIds, campaignId }),
  })
}

export async function getLeadStats(clientId?: string, leadListId?: string) {
  const query = new URLSearchParams()
  if (clientId) query.set("clientId", clientId)
  if (leadListId) query.set("leadListId", leadListId)
  const qs = query.toString()
  return request<{ total: number; byStatus: { new: number; contacted: number; qualified: number; converted: number; lost: number }; totalValue: number; newToday: number }>(`/leads/stats${qs ? `?${qs}` : ""}`)
}

// Audiences API

export interface AudienceContact {
  email?: string
  phone?: string
  firstName?: string
  lastName?: string
  company?: string
  country?: string
  zip?: string
  city?: string
  state?: string
  source?: string
  addedAt?: string
}

export interface AudienceCriteria {
  industry?: string[]
  locations?: string[]
  roles?: string[]
  keywords?: string[]
  interests?: string[]
  demographics?: {
    ageMin?: number
    ageMax?: number
    gender?: string
  }
  devices?: string[]
  lookbackDays?: number
  sourceAudienceId?: string
}

export interface Audience {
  _id: string
  name: string
  description: string
  type: 'paid_ads' | 'cold_outreach' | 'content' | 'seo'
  purpose: 'custom_audience' | 'lookalike_source' | 'retargeting' | 'lead_list'
  contacts?: AudienceContact[]
  criteria: AudienceCriteria
  size: number
  platforms: string[]
  status: 'ready' | 'building' | 'expired'
  clientId: string
  platformConfig?: any
  exclusions?: { audienceId: string; audienceName: string }[]
  qualityMetrics?: {
    matchRate: number
    emailCoverage: number
    phoneCoverage: number
    completeness: number
    lastCalculated?: string
  }
  estimatedReach?: { min: number; max: number; lastUpdated?: string }
  lookalikeConfig?: {
    sourceAudienceId?: string
    sourceAudienceName?: string
    similarityPercent?: number
    targetCountry?: string
    targetPlatform?: string
  }
  createdAt: string
  updatedAt: string
}

export async function getAudiences(params?: { clientId?: string; type?: string; purpose?: string; status?: string; search?: string; page?: number; limit?: number }) {
  const query = new URLSearchParams()
  if (params?.clientId) query.set("clientId", params.clientId)
  if (params?.type) query.set("type", params.type)
  if (params?.purpose) query.set("purpose", params.purpose)
  if (params?.status) query.set("status", params.status)
  if (params?.search) query.set("search", params.search)
  if (params?.page) query.set("page", String(params.page))
  if (params?.limit) query.set("limit", String(params.limit))
  const qs = query.toString()
  return request<Audience[]>(`/audiences${qs ? `?${qs}` : ""}`)
}

export async function createAudience(payload: Partial<Audience> & { name: string; clientId: string }) {
  return request<Audience>("/audiences", { method: "POST", body: JSON.stringify(payload) })
}

export async function getAudience(id: string) {
  return request<Audience>(`/audiences/${id}`)
}

export async function updateAudience(id: string, payload: Partial<Audience>) {
  return request<Audience>(`/audiences/${id}`, { method: "PUT", body: JSON.stringify(payload) })
}

export async function deleteAudience(id: string) {
  return request(`/audiences/${id}`, { method: "DELETE" })
}

export async function getAudienceContacts(id: string, params?: { page?: number; limit?: number; search?: string }) {
  const query = new URLSearchParams()
  if (params?.page) query.set("page", String(params.page))
  if (params?.limit) query.set("limit", String(params.limit))
  if (params?.search) query.set("search", params.search)
  const qs = query.toString()
  return request<AudienceContact[]>(`/audiences/${id}/contacts${qs ? `?${qs}` : ""}`)
}

export async function addAudienceContacts(id: string, contacts: Partial<AudienceContact>[]) {
  return request<Audience>(`/audiences/${id}/contacts`, { method: "POST", body: JSON.stringify({ contacts }) })
}

export async function removeAudienceContacts(id: string, payload: { emails?: string[]; phones?: string[] }) {
  return request<Audience>(`/audiences/${id}/contacts`, { method: "DELETE", body: JSON.stringify(payload) })
}

export function getAudienceExportUrl(id: string, format: 'meta' | 'google' = 'meta', hashed?: boolean) {
  return `${MARKETING_API_URL}/audiences/${id}/export?format=${format}${hashed ? '&hashed=true' : ''}`
}

export async function importAudienceCSV(id: string, file: File, format?: string): Promise<any> {
  const formData = new FormData()
  formData.append('file', file)
  if (format) formData.append('format', format)
  const res = await fetch(`${MARKETING_API_URL}/audiences/${id}/import`, {
    method: 'POST',
    headers: { 'X-Admin-Key': ADMIN_KEY },
    body: formData,
  })
  return res.json()
}

export function getAudienceTemplateUrl(format: 'meta' | 'google'): string {
  return `${MARKETING_API_URL}/audiences/templates/${format}`
}

export async function getAudienceCampaigns(id: string) {
  return request<any[]>(`/audiences/${id}/campaigns`)
}

export async function estimateAudienceReach(params: any) {
  const res = await request<{ min: number; max: number; type: string }>('/audiences/estimate-reach', {
    method: 'POST',
    body: JSON.stringify(params),
  })
  const payload = (res.data as any)?.data || res.data
  if (payload && typeof payload.min === 'number' && typeof payload.max === 'number') {
    const type = params?.type
    const cap = type === 'cold_outreach' ? 50000 : 5000000
    payload.min = Math.max(0, Math.min(payload.min, cap))
    payload.max = Math.max(0, Math.min(payload.max, cap))
  }
  return res
}

export async function addAudienceExclusion(id: string, excludedId: string) {
  return request<Audience>(`/audiences/${id}/exclusions`, {
    method: 'POST',
    body: JSON.stringify({ audienceId: excludedId }),
  })
}

export async function removeAudienceExclusion(id: string, excludedId: string) {
  return request<Audience>(`/audiences/${id}/exclusions/${excludedId}`, { method: 'DELETE' })
}

// Lead Batches
export async function createLeadBatch(params: { criteria?: any; sources?: string[]; clientId: string; leadListId?: string; audienceId?: string; maxResults?: number }) {
  return request('/lead-gen/batch', { method: 'POST', body: JSON.stringify(params) })
}

export async function getLeadBatches(params: { clientId: string; leadListId?: string; page?: number; limit?: number }) {
  const query = new URLSearchParams()
  query.set('clientId', params.clientId)
  if (params.leadListId) query.set('leadListId', params.leadListId)
  if (params.page) query.set('page', String(params.page))
  if (params.limit) query.set('limit', String(params.limit))
  return request(`/lead-gen/batches?${query}`)
}

export async function getLeadBatch(batchId: string) {
  return request(`/lead-gen/batches/${batchId}`)
}

export async function deleteLeadBatch(batchId: string) {
  return request(`/lead-gen/batches/${batchId}`, { method: 'DELETE' })
}

// Lead Generation
export async function searchLeads(params: { audienceId?: string; criteria?: any; sources?: string[]; clientId: string; leadListId?: string }) {
  return request<{ leads: any[]; count: number; sources: string[] }>('/lead-gen/search', {
    method: 'POST',
    body: JSON.stringify(params),
  })
}

export async function saveGeneratedLeads(params: { leads: any[]; audienceId?: string; clientId: string; leadListId?: string }) {
  return request<{ savedLeads: number; savedToAudience: number }>('/lead-gen/save', {
    method: 'POST',
    body: JSON.stringify(params),
  })
}

export async function getLeadGenSources() {
  return request<any>('/lead-gen/sources')
}

// Audience Analytics
export async function getAudiencesAnalyticsSummary(clientId?: string) {
  const params = clientId ? `?clientId=${clientId}` : ''
  return request<any[]>(`/audience-analytics/summary${params}`)
}

export async function getAudienceAnalytics(id: string) {
  return request<any>(`/audience-analytics/${id}`)
}

export async function checkAudienceOverlap(id1: string, id2: string) {
  return request<any>('/audience-analytics/overlap', {
    method: 'POST',
    body: JSON.stringify({ audienceId1: id1, audienceId2: id2 }),
  })
}

export async function getAudienceSyncs(id: string) {
  return request<any[]>(`/audience-analytics/syncs/${id}`)
}

export async function syncAudienceToPlatform(id: string, platform: string) {
  return request<any>(`/audience-analytics/sync/${id}`, {
    method: 'POST',
    body: JSON.stringify({ platform }),
  })
}

// --- Feedback ---

export async function getFeedback(params: {
  portal?: string; userType?: string; feedbackType?: string;
  category?: string; status?: string; search?: string;
  sort?: string; page?: number; limit?: number; clientId?: string;
  includeMainPortal?: string;
}) {
  const qs = new URLSearchParams()
  Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== '') qs.set(k, String(v)) })
  return request<any>(`/feedback?${qs.toString()}`)
}

export async function getFeedbackStats(portal?: string, userType?: string) {
  const qs = new URLSearchParams()
  if (portal) qs.set('portal', portal)
  if (userType) qs.set('userType', userType)
  return request<any>(`/feedback/stats?${qs.toString()}`)
}

export async function getFeedbackById(id: string) {
  return request<any>(`/feedback/${id}`)
}

export async function createFeedback(data: any) {
  return request<any>('/feedback', { method: 'POST', body: JSON.stringify(data) })
}

export async function updateFeedback(id: string, data: any) {
  return request<any>(`/feedback/${id}`, { method: 'PUT', body: JSON.stringify(data) })
}

export async function deleteFeedback(id: string) {
  return request<any>(`/feedback/${id}`, { method: 'DELETE' })
}

export async function upvoteFeedback(id: string, userId: string) {
  return request<any>(`/feedback/${id}/upvote`, { method: 'POST', body: JSON.stringify({ userId }) })
}

export async function replyToFeedback(id: string, data: { senderId: string; senderName: string; senderRole: string; message: string }) {
  return request<any>(`/feedback/${id}/reply`, { method: 'POST', body: JSON.stringify(data) })
}

// --- Admin Notifications ---
export async function getAdminNotifications(params: { type?: string; read?: string; page?: number; limit?: number } = {}) {
  const qs = new URLSearchParams()
  Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== '') qs.set(k, String(v)) })
  return request<any>(`/admin-notifications?${qs.toString()}`)
}

export async function getAdminNotificationCounts() {
  return request<{ unread: number; total: number }>('/admin-notifications/counts')
}

export async function markAdminNotificationRead(id: string) {
  return request<any>(`/admin-notifications/${id}/read`, { method: 'PATCH' })
}

export async function markAllAdminNotificationsRead() {
  return request<any>('/admin-notifications/mark-all-read', { method: 'PATCH' })
}

export async function connectCampaignIntegration(campaignId: string, platform: string) {
  return request<any>('/integrations/connect', {
    method: 'POST',
    body: JSON.stringify({ campaignId, platform }),
  })
}

export async function getCampaignIntegrations(campaignId: string) {
  return request<any>(`/integrations/${campaignId}`)
}

export async function provisionOutreachCluster(payload: {
  campaignId: string
  name?: string
  domainsCount?: number
  mailboxesPerDomain?: number
  dailySendPerMailbox?: number
  smsProvider?: string
}) {
  return request<any>('/outreach-clusters/provision', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export async function getOutreachCluster(campaignId: string) {
  return request<any>(`/outreach-clusters/${campaignId}`)
}

export async function getPendingClusterApprovals() {
  return request<any>('/outreach-clusters/jobs/pending-approvals')
}

export async function approveClusterJob(jobId: string) {
  return request<any>(`/outreach-clusters/jobs/${jobId}/approve`, { method: 'POST' })
}

export async function rejectClusterJob(jobId: string, reason: string) {
  return request<any>(`/outreach-clusters/jobs/${jobId}/reject`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  })
}

export async function getClusterControlPlane(clusterId: string) {
  return request<any>(`/outreach-clusters/clusters/${clusterId}/control-plane`)
}

export async function triggerClusterKillSwitch(clusterId: string) {
  return request<any>(`/outreach-clusters/clusters/${clusterId}/kill-switch`, { method: 'POST' })
}

// --- AI Insights ---

export async function getAIInsights(refresh = false) {
  return request<any>(`/ai-insights${refresh ? '?refresh=true' : ''}`)
}

export async function askAIInsights(question: string) {
  return request<any>('/ai-insights/ask', { method: 'POST', body: JSON.stringify({ question }) })
}

// --- Messaging / Contacts / Activity ---

export interface ActivityContactSummary {
  _id?: string
  firstName?: string
  lastName?: string
  email?: string
  phone?: string
  company?: string
}

export interface ActivityLogItem {
  _id: string
  clientId: string
  contactId?: string | ActivityContactSummary
  activityType: string
  channel: 'email' | 'sms'
  referenceId?: string
  metadata?: Record<string, any>
  occurredAt: string
  createdAt: string
}

export interface ContactRecord {
  _id: string
  clientId: string
  firstName?: string
  lastName?: string
  email?: string
  phone?: string
  company?: string
  tags?: string[]
  source?: string
  optOutEmail?: boolean
  optOutSms?: boolean
  lastActivityAt?: string
  createdAt: string
  updatedAt: string
}

export async function getActivities(params?: {
  clientId?: string
  contactId?: string
  channel?: string
  type?: string
  startDate?: string
  endDate?: string
  page?: number
  limit?: number
}) {
  const query = new URLSearchParams()
  if (params?.clientId) query.set('clientId', params.clientId)
  if (params?.contactId) query.set('contactId', params.contactId)
  if (params?.channel) query.set('channel', params.channel)
  if (params?.type) query.set('type', params.type)
  if (params?.startDate) query.set('startDate', params.startDate)
  if (params?.endDate) query.set('endDate', params.endDate)
  if (params?.page) query.set('page', String(params.page))
  if (params?.limit) query.set('limit', String(params.limit))
  const qs = query.toString()
  return request<{ activities: ActivityLogItem[]; total: number; page: number; limit: number }>(`/activities${qs ? `?${qs}` : ''}`)
}

export async function getDashboardMessagingStats(params?: { clientId?: string; channel?: string; startDate?: string; endDate?: string }) {
  const query = new URLSearchParams()
  if (params?.clientId) query.set('clientId', params.clientId)
  if (params?.channel) query.set('channel', params.channel)
  if (params?.startDate) query.set('startDate', params.startDate)
  if (params?.endDate) query.set('endDate', params.endDate)
  const qs = query.toString()
  return request<any>(`/dashboard/messaging-stats${qs ? `?${qs}` : ''}`)
}

export interface EmailMessagingStats {
  total: number
  queued: number
  sent: number
  delivered: number
  opened: number
  clicked: number
  failed: number
  bounced: number
  unsubscribed: number
  cancelled: number
  deliveryRate: number
  openRate: number
  clickRate: number
  failureRate: number
}

export interface SmsMessagingStats {
  total: number
  queued: number
  sent: number
  delivered: number
  failed: number
  cancelled: number
  deliveryRate: number
  failureRate: number
  optedOut?: number
  optOutRate?: number
  twilioErrorCodes?: Array<{ code: string; count: number; message?: string }>
  topTwilioErrorCodes?: Array<{ code: string; count: number; message?: string }>
}

export interface MessagingSummary {
  email: EmailMessagingStats
  sms: SmsMessagingStats
  combined: {
    total: number
    sent: number
    delivered: number
    engaged: number
    failed: number
  }
}

export async function getMessagingSummary(params?: {
  clientId?: string
  campaignId?: string
  draftId?: string
  from?: string
  to?: string
}) {
  const query = new URLSearchParams()
  if (params?.clientId) query.set('clientId', params.clientId)
  if (params?.campaignId) query.set('campaignId', params.campaignId)
  if (params?.draftId) query.set('draftId', params.draftId)
  if (params?.from) query.set('from', params.from)
  if (params?.to) query.set('to', params.to)
  const qs = query.toString()
  return request<MessagingSummary>(`/dashboard/messaging-summary${qs ? `?${qs}` : ''}`)
}

export async function getEmailDraftStats(id: string) {
  return request<EmailMessagingStats & { draftId: string }>(`/email-drafts/${id}/stats`)
}

export async function getSmsDraftStats(id: string) {
  return request<SmsMessagingStats & { draftId: string }>(`/sms-drafts/${id}/stats`)
}

export async function getRecentMessagingActivity(params?: { clientId?: string; limit?: number }) {
  const query = new URLSearchParams()
  if (params?.clientId) query.set('clientId', params.clientId)
  if (params?.limit) query.set('limit', String(params.limit))
  const qs = query.toString()
  return request<Array<ActivityLogItem & { id: string }>>(`/dashboard/recent-messaging-activity${qs ? `?${qs}` : ''}`)
}

export async function getDashboardActivitySummary(params?: { clientId?: string; days?: number }) {
  const query = new URLSearchParams()
  if (params?.clientId) query.set('clientId', params.clientId)
  if (params?.days) query.set('days', String(params.days))
  const qs = query.toString()
  return request<any[]>(`/dashboard/activity-summary${qs ? `?${qs}` : ''}`)
}

export async function getDashboardContactsSummary(params?: { clientId?: string }) {
  const query = new URLSearchParams()
  if (params?.clientId) query.set('clientId', params.clientId)
  const qs = query.toString()
  return request<any>(`/dashboard/contacts-summary${qs ? `?${qs}` : ''}`)
}

export async function getContacts(params?: {
  clientId?: string
  search?: string
  source?: string
  tags?: string
  page?: number
  limit?: number
}) {
  const query = new URLSearchParams()
  if (params?.clientId) query.set('clientId', params.clientId)
  if (params?.search) query.set('search', params.search)
  if (params?.source) query.set('source', params.source)
  if (params?.tags) query.set('tags', params.tags)
  if (params?.page) query.set('page', String(params.page))
  if (params?.limit) query.set('limit', String(params.limit))
  const qs = query.toString()
  return request<{ contacts: ContactRecord[]; total: number; page: number; limit: number }>(`/contacts${qs ? `?${qs}` : ''}`)
}

export interface ContactList {
  _id: string
  clientId: string
  name: string
  description?: string
  memberCount: number
  createdAt?: string
  updatedAt?: string
}

export async function getContactLists(params?: {
  clientId?: string
  search?: string
  page?: number
  limit?: number
}) {
  const query = new URLSearchParams()
  if (params?.clientId) query.set('clientId', params.clientId)
  if (params?.search) query.set('search', params.search)
  if (params?.page) query.set('page', String(params.page))
  if (params?.limit) query.set('limit', String(params.limit))
  const qs = query.toString()
  return request<{ lists: ContactList[]; total: number; page: number; limit: number }>(`/contacts/lists${qs ? `?${qs}` : ''}`)
}

export interface EmailDraft {
  _id: string
  clientId: string
  campaignId?: string | null
  name: string
  subject: string
  previewText?: string
  bodyHtml: string
  bodyText?: string
  fromName?: string
  fromEmail?: string
  replyTo?: string
  status: 'draft' | 'archived'
  lastBulkSendJobId?: string
  totalRecipients?: number
  createdAt?: string
  updatedAt?: string
}

export async function getEmailDrafts(params?: {
  clientId?: string
  status?: string
  search?: string
  page?: number
  limit?: number
}) {
  const query = new URLSearchParams()
  if (params?.clientId) query.set('clientId', params.clientId)
  if (params?.status) query.set('status', params.status)
  if (params?.search) query.set('search', params.search)
  if (params?.page) query.set('page', String(params.page))
  if (params?.limit) query.set('limit', String(params.limit))
  const qs = query.toString()
  return request<{ drafts: EmailDraft[]; total: number; page: number; limit: number }>(`/email-drafts${qs ? `?${qs}` : ''}`)
}

export async function createEmailDraft(payload: Partial<EmailDraft> & {
  clientId: string
  name: string
  subject: string
  bodyHtml: string
}) {
  return request<EmailDraft>('/email-drafts', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export async function updateEmailDraft(id: string, payload: Partial<EmailDraft>) {
  return request<EmailDraft>(`/email-drafts/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  })
}

export async function previewEmailDraft(id: string, payload?: {
  contactId?: string
  mergeData?: Record<string, string>
  fallbacks?: Record<string, string>
}) {
  return request<{
    desktopHtml: string
    mobileHtml: string
    subject: string
    preheader: string
    missingMergeTags: string[]
  }>(`/email-drafts/${id}/preview`, {
    method: 'POST',
    body: JSON.stringify(payload || {}),
  })
}

export async function sendEmailDraftTest(id: string, payload: {
  toEmail: string
  contactId?: string
}) {
  return request<any>(`/email-drafts/${id}/test-send`, {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export async function sendEmailDraftCampaign(id: string, payload: {
  recipientType: 'contacts' | 'contact_list'
  contactIds?: string[]
  contactListId?: string
  provider: 'smtp' | 'sendgrid'
}) {
  return request<{
    jobId: string
    draftId: string
    queuedRecipients: number
    provider: string
    recipientType: string
    contactListId?: string
  }>(`/email-drafts/${id}/send`, {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export interface SmsSegmentInfo {
  encoding: 'GSM-7' | 'UCS-2'
  characterCount: number
  segmentCount: number
  charactersPerSegment: number
  remainingInSegment: number
}

export interface SmsPreview {
  body: string
  segmentInfo: SmsSegmentInfo
  optOutFooter: string
}

export interface SmsDraft {
  _id: string
  clientId: string
  campaignId?: string | null
  name: string
  body: string
  fromNumber?: string
  tags?: string[]
  status: 'draft' | 'archived'
  lastTestSentAt?: string
  smsPreview?: SmsPreview
  createdAt?: string
  updatedAt?: string
}

export async function getSmsDrafts(params?: {
  clientId?: string
  campaignId?: string
  status?: string
  search?: string
  page?: number
  limit?: number
}) {
  const query = new URLSearchParams()
  if (params?.clientId) query.set('clientId', params.clientId)
  if (params?.campaignId) query.set('campaignId', params.campaignId)
  if (params?.status) query.set('status', params.status)
  if (params?.search) query.set('search', params.search)
  if (params?.page) query.set('page', String(params.page))
  if (params?.limit) query.set('limit', String(params.limit))
  const qs = query.toString()
  return request<{ drafts: SmsDraft[]; total: number; page: number; limit: number }>(`/sms-drafts${qs ? `?${qs}` : ''}`)
}

export async function createSmsDraft(payload: Partial<SmsDraft> & {
  clientId: string
  name: string
  body: string
}) {
  return request<SmsDraft>('/sms-drafts', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export async function updateSmsDraft(id: string, payload: Partial<SmsDraft>) {
  return request<SmsDraft>(`/sms-drafts/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  })
}

export async function previewSmsDraft(id: string) {
  return request<SmsDraft>(`/sms-drafts/${id}`)
}

export async function sendSmsDraftTest(id: string, payload: {
  toPhone: string
  contactId?: string
}) {
  return request<{
    sid: string
    status: string
    fromNumber: string
    providerMode: string
    conversationId: string
    body: string
    segmentInfo: SmsSegmentInfo
  }>(`/sms-drafts/${id}/test-send`, {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export async function sendSmsDraftCampaign(id: string, payload: {
  recipientType: "contact_list"
  contactListId: string
}) {
  return request<{
    draftId: string
    queuedRecipients: number
    skippedRecipients: number
    recipientType: "contact_list"
    contactListId: string
    jobIds: string[]
    messageRecipientIds: string[]
  }>(`/sms-drafts/${id}/send`, {
    method: "POST",
    body: JSON.stringify(payload),
  })
}

// --- Social Connections & Posts ---

export async function getCampaignSocialConnections(campaignId: string) {
  return request<any[]>(`/social/connections/campaign/${campaignId}`)
}

export async function getClientSocialConnections(clientId: string) {
  return request<any[]>(`/social/connections?clientId=${clientId}`)
}

export async function assignSocialConnection(campaignId: string, platform: string, connectionId: string) {
  return request<any>(`/social/connections/campaign/${campaignId}/assign`, {
    method: 'POST',
    body: JSON.stringify({ platform, connectionId }),
  })
}

export async function unassignSocialConnection(campaignId: string, platform: string) {
  return request<any>(`/social/connections/campaign/${campaignId}/assign/${platform}`, {
    method: 'DELETE',
  })
}

export async function getCampaignPosts(campaignId: string) {
  return request<any[]>(`/social/posts/${campaignId}`)
}

export async function postCreativeToSocial(payload: {
  campaignId: string
  clientId: string
  creativeId: string
  platform: string
  caption?: string
  scheduledAt?: string
}) {
  return request<any>('/social/post', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export async function refreshPostAnalytics(postId: string) {
  return request<any>(`/social/analytics/${postId}`, { method: 'POST' })
}

export async function getCampaignCalendar(campaignId: string) {
  return request<any[]>(`/calendar/${campaignId}`)
}

export async function cancelScheduledPost(postRecordId: string) {
  return request<any>(`/calendar/${postRecordId}`, { method: 'DELETE' })
}

export async function reschedulePost(postRecordId: string, scheduledAt: string) {
  return request<any>(`/calendar/${postRecordId}/reschedule`, {
    method: 'PATCH',
    body: JSON.stringify({ scheduledAt }),
  })
}

// ── Password Reset ────────────────────────────────────────────────────────────
async function authRequest<T>(endpoint: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((options.headers as Record<string, string>) || {}),
  }

  const res = await fetch(`/api/marketing-auth${endpoint}`, {
    ...options,
    headers,
  })
  const data = await res.json()
  if (!res.ok && !data.success) throw new Error(data.message || 'Request failed')
  return data
}

export async function loginAdmin(email: string, password: string, captchaToken?: string) {
  return authRequest<MarketingAuthResponse>('/login?portal=admin', {
    method: 'POST',
    headers: { 'X-Portal-Type': 'admin' },
    body: JSON.stringify({ email, password, captchaToken }),
  })
}

export async function verifyAdminOtp(email: string, code: string) {
  return authRequest<MarketingAuthResponse>('/verify-otp-login', {
    method: 'POST',
    body: JSON.stringify({ email, code }),
  })
}

export async function resendAdminOtp(email: string) {
  return authRequest<{ email: string }>('/resend-otp-login', {
    method: 'POST',
    body: JSON.stringify({ email }),
  })
}

export async function signupAdmin(payload: {
  email: string
  password: string
  firstName?: string
  lastName?: string
  businessName?: string
  mobileNumber?: string
  captchaToken?: string
}) {
  return authRequest<{ token: string; user: Record<string, unknown> }>('/signup', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export async function forgotPasswordAdmin(email: string) {
  return authRequest('/forgot-password', {
    method: 'POST',
    headers: { 'X-Portal-Type': 'admin' },
    body: JSON.stringify({ email, portal: 'admin' }),
  })
}

export async function resetPasswordAdmin(token: string, password: string) {
  return authRequest('/reset-password', {
    method: 'POST',
    headers: { 'X-Portal-Type': 'admin' },
    body: JSON.stringify({ token, password, portal: 'admin' }),
  })
}

export async function updateProfileAdmin(data: { firstName?: string; lastName?: string; phone?: string; company?: string }) {
  return authRequest('/profile', { method: 'PATCH', body: JSON.stringify(data) })
}

export async function getAnalyticsSummary(clientId?: string) {
  return request<any>(`/social/analytics-summary${clientId ? `?clientId=${clientId}` : ''}`)
}

export async function refreshAllAnalytics(clientId?: string) {
  return request<any>('/social/analytics-refresh', { method: 'POST', body: JSON.stringify({ clientId }) })
}

// ─── Lead CSV Upload ──────────────────────────────────────────────────────────
export async function uploadLeadsCsv(file: File, clientId: string, notifyEmail: string, leadListId?: string) {
  const form = new FormData()
  form.append('file', file)
  form.append('clientId', clientId)
  form.append('notifyEmail', notifyEmail)
  if (leadListId) form.append('leadListId', leadListId)
  const ADMIN_KEY_VAL = process.env.NEXT_PUBLIC_ADMIN_API_KEY || "24hr-mktg-admin-key-2026"
  const res = await fetch(`${MARKETING_API_URL}/leads/upload-csv`, {
    method: 'POST',
    headers: { 'x-admin-key': ADMIN_KEY_VAL },
    body: form,
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: res.statusText }))
    throw new Error(err.message || 'Upload failed')
  }
  return res.json()
}

export async function getLeadUploads(clientId: string) {
  return request<any>(`/leads/uploads?clientId=${clientId}`)
}

export async function getLeadUploadStatus(uploadId: string) {
  return request<any>(`/leads/uploads/${uploadId}`)
}

// ─── Team / Users API ───────────────────────────────────────────────────

export interface TeamUser {
  _id: string
  email: string
  firstName: string
  lastName: string
  role: "admin" | "manager" | "viewer"
  isActive: boolean
  assignedClientIds: string[]
  createdAt: string
}

export async function getTeamUsers(): Promise<ApiResponse<TeamUser[]>> {
  return request<TeamUser[]>("/users")
}

export async function updateUserAssignedClients(userId: string, assignedClientIds: string[]): Promise<ApiResponse<TeamUser>> {
  return request<TeamUser>(`/users/${userId}/assigned-clients`, {
    method: "PUT",
    body: JSON.stringify({ assignedClientIds }),
  })
}

export async function updateUser(userId: string, data: Partial<TeamUser>): Promise<ApiResponse<TeamUser>> {
  return request<TeamUser>(`/users/${userId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  })
}

export async function inviteTeamUser(data: { email: string; role: string; firstName?: string; lastName?: string; assignedClientIds?: string[] }): Promise<ApiResponse<any>> {
  return request<any>("/users/invite", {
    method: "POST",
    body: JSON.stringify(data),
  })
}

// Aliases for team management page
export async function getUsers(): Promise<ApiResponse<TeamUser[]>> {
  return getTeamUsers()
}

export async function assignClientsToManager(userId: string, clientIds: string[]): Promise<ApiResponse<TeamUser>> {
  return request<TeamUser>(`/auth/assign-clients/${userId}`, {
    method: "PUT",
    body: JSON.stringify({ clientIds }),
  })
}

// Growth Network APIs

export type GrowthAudienceType = "fleet" | "service_provider"
export type GrowthProfileStage =
  | "lead_captured"
  | "contacted"
  | "qualification_started"
  | "qualification_complete"
  | "territory_assigned"
  | "research_started"
  | "gap_analysis_sent"
  | "market_review"
  | "enrolled"
  | "activated"
  | "opportunities_sent"
  | "conversion_offered"
  | "subscriber"
  | "lost"

export interface GrowthNetworkProfile {
  _id: string
  clientId: string
  leadId?: string
  contactId?: string
  hubspotId?: string
  hubspotObjectType?: "fleet_profile" | "provider_profile"
  sourceSystem?: string
  audienceType: GrowthAudienceType
  stage: GrowthProfileStage
  name: string
  companyName?: string
  phone?: string
  email?: string
  website?: string
  state?: string
  city?: string
  zipCode?: string
  channels?: string[]
  fleet?: Record<string, any>
  provider?: Record<string, any>
  research?: Record<string, any>
  scoring?: Record<string, any>
  territoryId?: string
  territoryName?: string
  routing?: Record<string, any>
  conversion?: Record<string, any>
  tags?: string[]
  notes?: string
  createdAt?: string
  updatedAt?: string
}

export interface GrowthTerritory {
  _id: string
  clientId: string
  name: string
  state?: string
  city?: string
  zipCodes?: string[]
  targetMarkets?: string[]
  desiredProviderCount?: number
  fleetLeadCount?: number
  providerLeadCount?: number
  scores?: {
    demandScore?: number
    supplyScore?: number
    coverageScore?: number
    gapScore?: number
    marketReadinessScore?: number
  }
  recommendation?: "recruit_providers" | "recruit_fleets" | "ready_to_route" | "monitor"
  status?: "planning" | "recruiting_providers" | "recruiting_fleets" | "routing" | "conversion" | "paused"
  notes?: string
  lastScoredAt?: string
  createdAt?: string
  updatedAt?: string
}

export interface GrowthOpportunity {
  _id: string
  clientId: string
  territoryId?: string
  fleetProfileId?: string
  providerProfileId?: string
  source?: "dispatch" | "manual" | "campaign" | "ai_agent"
  status: "identified" | "sent" | "accepted" | "declined" | "completed" | "paid" | "cancelled"
  customerRequestSummary?: string
  workType?: string
  city?: string
  state?: string
  zipCode?: string
  estimatedRevenue?: number
  actualRevenue?: number
  satisfactionScore?: number
  routedAt?: string
  completedAt?: string
  paymentTrackedAt?: string
  notes?: string
  createdAt?: string
  updatedAt?: string
}

export interface GrowthNetworkSummary {
  totals: {
    fleets: number
    providers: number
    territories: number
    readyMarkets: number
    routedOpportunities: number
    conversionReady: number
    completedRevenue: number
  }
  byAudience: Record<string, number>
  byStage: Record<string, number>
  opportunityByStatus: Record<string, { count: number; estimatedRevenue: number; actualRevenue: number }>
  territories: GrowthTerritory[]
  recentProfiles: GrowthNetworkProfile[]
}

function growthNetworkQuery(params?: Record<string, string | number | undefined>) {
  const query = new URLSearchParams()
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== "") query.set(key, String(value))
  })
  const qs = query.toString()
  return qs ? `?${qs}` : ""
}

export async function getGrowthNetworkSummary(params?: { clientId?: string }) {
  return request<GrowthNetworkSummary>(`/growth-network/summary${growthNetworkQuery(params)}`)
}

export async function getGrowthNetworkProfiles(params?: {
  clientId?: string
  audienceType?: GrowthAudienceType
  stage?: string
  territoryId?: string
  search?: string
  page?: number
  limit?: number
}) {
  return request<{ profiles: GrowthNetworkProfile[]; total: number; page: number; limit: number }>(
    `/growth-network/profiles${growthNetworkQuery(params)}`
  )
}

export async function createGrowthNetworkProfile(payload: Partial<GrowthNetworkProfile> & { clientId: string; audienceType: GrowthAudienceType; name: string }) {
  return request<GrowthNetworkProfile>("/growth-network/profiles", {
    method: "POST",
    body: JSON.stringify(payload),
  })
}

export async function updateGrowthNetworkProfile(id: string, payload: Partial<GrowthNetworkProfile>) {
  return request<GrowthNetworkProfile>(`/growth-network/profiles/${id}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  })
}

export async function generateProviderGapAnalysis(id: string) {
  return request<{ profile: GrowthNetworkProfile; message: string }>(`/growth-network/profiles/${id}/gap-analysis`, {
    method: "POST",
  })
}

export async function generateProviderConversionOffer(id: string) {
  return request<{ profile: GrowthNetworkProfile; message: string }>(`/growth-network/profiles/${id}/conversion-offer`, {
    method: "POST",
  })
}

export async function getGrowthTerritories(params?: { clientId?: string; recommendation?: string; status?: string; page?: number; limit?: number }) {
  return request<{ territories: GrowthTerritory[]; total: number; page: number; limit: number }>(
    `/growth-network/territories${growthNetworkQuery(params)}`
  )
}

export async function createGrowthTerritory(payload: Partial<GrowthTerritory> & { clientId: string; name: string }) {
  return request<GrowthTerritory>("/growth-network/territories", {
    method: "POST",
    body: JSON.stringify(payload),
  })
}

export async function updateGrowthTerritory(id: string, payload: Partial<GrowthTerritory>) {
  return request<GrowthTerritory>(`/growth-network/territories/${id}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  })
}

export async function scoreGrowthTerritory(id: string) {
  return request<GrowthTerritory>(`/growth-network/territories/${id}/score`, {
    method: "POST",
  })
}

export async function getGrowthOpportunities(params?: {
  clientId?: string
  status?: string
  territoryId?: string
  providerProfileId?: string
  fleetProfileId?: string
  page?: number
  limit?: number
}) {
  return request<{ opportunities: GrowthOpportunity[]; total: number; page: number; limit: number }>(
    `/growth-network/opportunities${growthNetworkQuery(params)}`
  )
}

export async function createGrowthOpportunity(payload: Partial<GrowthOpportunity> & { clientId: string }) {
  return request<GrowthOpportunity>("/growth-network/opportunities", {
    method: "POST",
    body: JSON.stringify(payload),
  })
}

export async function updateGrowthOpportunityStatus(id: string, payload: Pick<Partial<GrowthOpportunity>, "status" | "actualRevenue" | "satisfactionScore" | "notes">) {
  return request<GrowthOpportunity>(`/growth-network/opportunities/${id}/status`, {
    method: "PUT",
    body: JSON.stringify(payload),
  })
}

// ─── Upload with progress (XMLHttpRequest) ──────────────────────────────

export function uploadCampaignMediaWithProgress(
  files: File[],
  onProgress: (loaded: number, total: number) => void
): { promise: Promise<ApiResponse<{ urls: string[] }>>; abort: () => void } {
  const formData = new FormData()
  files.forEach((f) => formData.append("files", f))

  const xhr = new XMLHttpRequest()

  const promise = new Promise<ApiResponse<{ urls: string[] }>>((resolve, reject) => {
    xhr.upload.addEventListener("progress", (e) => {
      if (e.lengthComputable) onProgress(e.loaded, e.total)
    })

    xhr.addEventListener("load", () => {
      try {
        const json = JSON.parse(xhr.responseText)
        if (xhr.status >= 200 && xhr.status < 300) resolve(json)
        else reject(new Error(json.message || "Upload failed"))
      } catch {
        reject(new Error("Upload failed"))
      }
    })

    xhr.addEventListener("error", () => reject(new Error("Upload failed")))
    xhr.addEventListener("abort", () => reject(new Error("Upload cancelled")))

    xhr.open("POST", `${MARKETING_API_URL}/campaigns/upload-media`)
    xhr.setRequestHeader("X-Admin-Key", ADMIN_KEY)
    xhr.send(formData)
  })

  return { promise, abort: () => xhr.abort() }
}

// ─── Per-file upload with progress tracking ────────────────────────────────
export interface PerFileProgress {
  fileId: string
  fileName: string
  loaded: number
  total: number
  percentage: number
  status: 'pending' | 'uploading' | 'success' | 'error'
  error?: string
  url?: string
}

export class PerFileUploadManager {
  private xhrs = new Map<string, XMLHttpRequest>()
  private progressCallbacks = new Map<string, (progress: PerFileProgress) => void>()
  private completionCallbacks = new Map<string, (result: PerFileProgress) => void>()

  onProgress(fileId: string, callback: (progress: PerFileProgress) => void) {
    this.progressCallbacks.set(fileId, callback)
  }

  onCompletion(fileId: string, callback: (result: PerFileProgress) => void) {
    this.completionCallbacks.set(fileId, callback)
  }

  private emitProgress(progress: PerFileProgress) {
    this.progressCallbacks.get(progress.fileId)?.(progress)
  }

  private emitCompletion(result: PerFileProgress) {
    this.completionCallbacks.get(result.fileId)?.(result)
  }

  uploadFile(fileId: string, file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const formData = new FormData()
      formData.append('files', file)

      const xhr = new XMLHttpRequest()
      this.xhrs.set(fileId, xhr)

      const emitProgress = (loaded: number, total: number) => {
        const progress: PerFileProgress = {
          fileId,
          fileName: file.name,
          loaded,
          total,
          percentage: Math.round((loaded / total) * 100),
          status: 'uploading',
        }
        this.emitProgress(progress)
      }

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) emitProgress(e.loaded, e.total)
      })

      xhr.addEventListener('load', () => {
        this.xhrs.delete(fileId)
        try {
          const json = JSON.parse(xhr.responseText)
          if (xhr.status >= 200 && xhr.status < 300) {
            const url = json.data?.urls?.[0] || json.urls?.[0]
            if (url) {
              const result: PerFileProgress = {
                fileId,
                fileName: file.name,
                loaded: file.size,
                total: file.size,
                percentage: 100,
                status: 'success',
                url,
              }
              this.emitCompletion(result)
              resolve(url)
            } else {
              throw new Error('No URL returned from upload')
            }
          } else {
            throw new Error(json.message || `Upload failed with status ${xhr.status}`)
          }
        } catch (e) {
          const error = e instanceof Error ? e.message : 'Upload failed'
          const result: PerFileProgress = {
            fileId,
            fileName: file.name,
            loaded: 0,
            total: file.size,
            percentage: 0,
            status: 'error',
            error,
          }
          this.emitCompletion(result)
          reject(new Error(error))
        }
      })

      xhr.addEventListener('error', () => {
        this.xhrs.delete(fileId)
        const error = 'Network error during upload'
        const result: PerFileProgress = {
          fileId,
          fileName: file.name,
          loaded: 0,
          total: file.size,
          percentage: 0,
          status: 'error',
          error,
        }
        this.emitCompletion(result)
        reject(new Error(error))
      })

      xhr.addEventListener('abort', () => {
        this.xhrs.delete(fileId)
        const error = 'Upload cancelled'
        const result: PerFileProgress = {
          fileId,
          fileName: file.name,
          loaded: 0,
          total: file.size,
          percentage: 0,
          status: 'error',
          error,
        }
        this.emitCompletion(result)
        reject(new Error(error))
      })

      xhr.open('POST', `${MARKETING_API_URL}/campaigns/upload-media`)
      xhr.setRequestHeader('X-Admin-Key', ADMIN_KEY)
      xhr.send(formData)
    })
  }

  cancelFile(fileId: string) {
    const xhr = this.xhrs.get(fileId)
    if (xhr) {
      xhr.abort()
      this.xhrs.delete(fileId)
    }
  }

  cancelAll() {
    this.xhrs.forEach((xhr) => xhr.abort())
    this.xhrs.clear()
  }

  destroy() {
    this.cancelAll()
    this.progressCallbacks.clear()
    this.completionCallbacks.clear()
  }
}
